import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import MerchantSearchSelect from '../ui/merchant-search-select';
import MultiSelect from '../ui/multi-select';
import 'flag-icons/css/flag-icons.min.css';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  DollarSign, 
  CreditCard, 
  TrendingUp, 
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Filter,
  X,
  Store,
  BarChart3,
  CheckCircle,
  RefreshCw,
  Globe,
  AlertTriangle,
  Loader2,
  FileText
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, ComposedChart } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useReference } from '../../contexts/ReferenceContext';
import { 
  formatCurrencyForOverview, 
  formatNumberForOverview,
  getCurrencySymbol,
  formatCurrencySelector,
  getCurrencyDisplay 
} from '../../utils/currencyFormatter';
import { getCountryName } from '../../utils/countries';

// Channel filter is DB-driven: `channel` is an array of channel codes from the
// Channels settings table (via /api/reference/filters). No hardcoded groupings.
const appendChannelParams = (params, channel) => {
  if (Array.isArray(channel)) {
    channel.forEach(ch => params.append('channel', ch));
  } else if (channel && channel !== 'all') {
    params.append('channel', channel);
  }
};

// Shown inline on a card/chart when its analytics query times out on the server
// (HTTP 503). Distinguishes a genuine infrastructure hiccup from a real empty
// result so admins never misread a swallowed timeout as a false "$0 / no data".
const DataUnavailable = ({ onRetry, compact = false, testId = 'data-unavailable' }) => (
  <div
    data-testid={testId}
    className={`flex flex-col items-center justify-center text-center gap-2 ${compact ? 'py-4' : 'py-8'}`}
  >
    <AlertTriangle className="h-6 w-6 text-amber-500" />
    <p className="text-sm font-medium text-muted-foreground">Data temporarily unavailable</p>
    {onRetry && (
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        data-testid={`${testId}-retry-btn`}
        className="mt-1"
      >
        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
        Retry
      </Button>
    )}
  </div>
);

// True when an axios error is a server-surfaced analytics timeout (HTTP 503).
const isAnalyticsUnavailable = (err) => err?.response?.status === 503;

// Breakdown rows with no activity at all add noise to the Overview cards
// (e.g. "n/a"/"unknown" buckets that are all-declined show "$0 · 0"). Hide a row
// when both its displayed revenue AND displayed count are zero. Payment-method
// rows display `approved_count` (their `count` is the TOTAL, which can be
// non-zero even when approved is 0), so prefer `approved_count` when present;
// other facets expose the approved figure as `count`.
const hasBreakdownValue = (item) => {
  if (!item) return false;
  const rev = Number(item.revenue ?? item.total_amount ?? 0) || 0;
  const cnt = ('approved_count' in item)
    ? (Number(item.approved_count) || 0)
    : (Number(item.count) || 0);
  return rev !== 0 || cnt !== 0;
};

// Get current time in platform's timezone as formatted string
const getCurrentPlatformTime = (platformTimezone) => {
  const now = new Date();
  return now.toLocaleString('en-US', { 
    timeZone: platformTimezone || 'America/Mexico_City',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

// Helper function to check if last transaction is stale (older than 5 minutes)
// Transaction time is stored in platform's local timezone (without timezone info)
// We need to compare it with current time also in platform's local timezone
const isTransactionStale = (lastTransactionTime, platformTimezone) => {
  if (!lastTransactionTime) return false;
  
  try {
    // Get current time formatted in platform's timezone
    const now = new Date();
    const platformNowStr = now.toLocaleString('en-US', { 
      timeZone: platformTimezone || 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Parse transaction time (stored as local time string)
    // Format: "2026-04-06T05:43:48" or "2026-04-06 05:43:48"
    const txTimeStr = lastTransactionTime.replace('T', ' ').replace('Z', '');
    
    // Parse both as simple date strings for comparison
    const txParts = txTimeStr.match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2}):(\d{2}):(\d{2})/);
    const nowParts = platformNowStr.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})/);
    
    if (!txParts || !nowParts) {
      console.error('[Overview] Could not parse dates', { txTimeStr, platformNowStr });
      return true;
    }
    
    // Create comparable timestamps (treat both as same timezone)
    const txDate = new Date(
      parseInt(txParts[1]), parseInt(txParts[2]) - 1, parseInt(txParts[3]),
      parseInt(txParts[4]), parseInt(txParts[5]), parseInt(txParts[6])
    );
    const nowDate = new Date(
      parseInt(nowParts[3]), parseInt(nowParts[1]) - 1, parseInt(nowParts[2]),
      parseInt(nowParts[4]), parseInt(nowParts[5]), parseInt(nowParts[6])
    );
    
    // Calculate difference in minutes
    const diffMs = nowDate.getTime() - txDate.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    
    console.log('[Overview] Staleness check:', {
      transactionTimeRaw: lastTransactionTime,
      txParsed: `${txParts[1]}-${txParts[2]}-${txParts[3]} ${txParts[4]}:${txParts[5]}:${txParts[6]}`,
      platformNow: platformNowStr,
      nowParsed: `${nowParts[3]}-${nowParts[1]}-${nowParts[2]} ${nowParts[4]}:${nowParts[5]}:${nowParts[6]}`,
      diffMinutes: diffMinutes.toFixed(2),
      isStale: diffMinutes > 5
    });
    
    // Return true if more than 5 minutes old
    return diffMinutes > 5;
  } catch (error) {
    console.error('[Overview] Error checking transaction staleness:', error);
    return false;
  }
};

// Format time difference for display
const formatTimeDiff = (lastTransactionTime, platformTimezone) => {
  if (!lastTransactionTime) return '';
  
  try {
    // Get current time in platform's timezone
    const now = new Date();
    const platformNowStr = now.toLocaleString('en-US', { 
      timeZone: platformTimezone || 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Parse transaction time
    const txTimeStr = lastTransactionTime.replace('T', ' ').replace('Z', '');
    const txParts = txTimeStr.match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2}):(\d{2}):(\d{2})/);
    const nowParts = platformNowStr.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})/);
    
    if (!txParts || !nowParts) return '';
    
    const txDate = new Date(
      parseInt(txParts[1]), parseInt(txParts[2]) - 1, parseInt(txParts[3]),
      parseInt(txParts[4]), parseInt(txParts[5]), parseInt(txParts[6])
    );
    const nowDate = new Date(
      parseInt(nowParts[3]), parseInt(nowParts[1]) - 1, parseInt(nowParts[2]),
      parseInt(nowParts[4]), parseInt(nowParts[5]), parseInt(nowParts[6])
    );
    
    const diffMs = nowDate.getTime() - txDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'just now';
    }
  } catch (error) {
    return '';
  }
};

// Format transaction time for display - show as stored (no timezone conversion)
const formatInLocalTimezone = (localTime, platformTimezone) => {
  if (!localTime) return '';
  try {
    // Parse the time string directly and format it
    const txTimeStr = localTime.replace('T', ' ').replace('Z', '');
    const txParts = txTimeStr.match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2}):(\d{2}):(\d{2})/);
    
    if (!txParts) {
      return new Date(localTime).toLocaleString();
    }
    
    // Format as MM/DD/YYYY, HH:MM:SS AM/PM
    const year = txParts[1];
    const month = txParts[2];
    const day = txParts[3];
    let hour = parseInt(txParts[4]);
    const minute = txParts[5];
    const second = txParts[6];
    
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    const hourStr = hour.toString().padStart(2, '0');
    
    return `${month}/${day}/${year}, ${hourStr}:${minute}:${second} ${ampm}`;
  } catch (error) {
    return new Date(localTime).toLocaleString();
  }
};

// Helper function to convert period types to days
const getDaysFromPeriod = (period, customMonth = null, customYear = null) => {
  const now = new Date();
  
  switch (period) {
    case 'today':
      return 1;
    case 'last7days':
      return 7;
    case 'thismonth':
      // Get days from start of current month to today
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const diffTime = now - startOfMonth;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDays); // At least 1 day
    case 'thisyear':
      // Get days from start of current year to today
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const diffTimeYear = now - startOfYear;
      const diffDaysYear = Math.ceil(diffTimeYear / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDaysYear); // At least 1 day
    case 'custom':
      if (customYear && customMonth) {
        const customStart = new Date(customYear, customMonth - 1, 1);
        const customEnd = new Date(customYear, customMonth, 0, 23, 59, 59);
        const today = new Date();
        // If custom date is in future or current month, use today as end date
        const endDate = customYear > today.getFullYear() || 
                       (customYear === today.getFullYear() && customMonth > today.getMonth() + 1) ||
                       (customYear === today.getFullYear() && customMonth === today.getMonth() + 1) 
                       ? today : customEnd;
        const diffTime = endDate - customStart;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(1, diffDays);
      }
      return 30;
    default:
      return 30; // fallback
  }
};

const MetricCard = ({ title, value, icon: Icon, trend, trendValue, subtitle, testId }) => (
  <Card className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50/50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 relative z-10">
      <CardTitle className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{title}</CardTitle>
      <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md group-hover:shadow-lg transition-shadow duration-300">
        <Icon className="h-5 w-5" />
      </div>
    </CardHeader>
    <CardContent className="relative z-10">
      <div className="text-3xl font-bold text-gray-900 mb-2" data-testid={testId}>{value}</div>
      {subtitle && <div className="mb-3">{subtitle}</div>}
      {trend && trendValue != null && (
        <div className="flex items-center">
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            trend === 'up' 
              ? 'bg-green-100 text-green-700' 
              : trend === 'down'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {trend === 'up' ? (
              <ArrowUpRight className="h-3 w-3 mr-1" />
            ) : trend === 'down' ? (
              <ArrowDownRight className="h-3 w-3 mr-1" />
            ) : (
              <span className="mr-1">=</span>
            )}
            {parseFloat(trendValue).toFixed(1)}%
          </div>
          <span className="text-xs text-gray-500 ml-2">vs last month</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const RevenueChart = ({ currency, merchantId, selectedPlatform, backendUrl, channel }) => {
  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [monthlyChannelData, setMonthlyChannelData] = useState([]);
  const [monthlyProcessorData, setMonthlyProcessorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [chartMode, setChartMode] = useState('total'); // 'total', 'channel', or 'processor'
  const [metric, setMetric] = useState('revenue'); // 'revenue' or 'transactions'
  const { token } = useAuth();
  const { selectedPlatform: contextPlatform } = usePlatform();
  
  // Use platform from props first, then context
  const actualPlatform = selectedPlatform || contextPlatform || 'plt_default';
  
  // Fetch monthly data independently - always fetch full year
  useEffect(() => {
    const fetchMonthlyData = async () => {
      if (!token || !actualPlatform) {
        console.log('[Monthly Revenue Chart] Missing token or platform');
        return;
      }
      
      console.log('[Monthly Revenue Chart] Fetching full year data independently');
      setLoading(true);
      setUnavailable(false);
      
      try {
        // Scope to the CURRENT CALENDAR YEAR (Jan 1 → Dec 31). The chart is a
        // Jan–Dec calendar view, so a rolling 365-day window wrongly pulled in the
        // previous year's Oct/Nov/Dec and showed them as the current year's months
        // ("phantom November"). A bounded start_date also gives tighter index bounds.
        const targetYear = new Date().getFullYear();
        const params = new URLSearchParams({
          start_date: `${targetYear}-01-01`,
          days: '366',
          platform_id: actualPlatform
        });
        
        if (currency && currency !== 'all') {
          params.append('currency', currency);
        }
        
        if (merchantId && merchantId !== 'all') {
          params.append('merchant_id', merchantId);
        }
        
        appendChannelParams(params, channel);
        
        const API = `${backendUrl}/api`;
        const response = await axios.get(`${API}/analytics/monthly-revenue?${params}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        console.log('[Monthly Revenue Chart] API response received:', {
          monthlyRevenueCount: response.data.monthly_revenue?.length || 0,
          monthlyChannelCount: response.data.monthly_revenue_by_channel?.length || 0,
          monthlyProcessorCount: response.data.monthly_revenue_by_processor?.length || 0
        });
        
        setMonthlyRevenueData(response.data.monthly_revenue || []);
        setMonthlyChannelData(response.data.monthly_revenue_by_channel || []);
        setMonthlyProcessorData(response.data.monthly_revenue_by_processor || []);
      } catch (err) {
        console.error('[Monthly Revenue Chart] Error fetching data:', err.response?.data || err.message);
        if (isAnalyticsUnavailable(err)) setUnavailable(true);
        setMonthlyRevenueData([]);
        setMonthlyChannelData([]);
        setMonthlyProcessorData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMonthlyData();
  }, [token, actualPlatform, currency, merchantId, backendUrl, reloadKey, Array.isArray(channel) ? channel.join(',') : channel]);
  
  // Get unique processors from data
  const getProcessors = () => {
    const processors = new Set();
    monthlyProcessorData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key.endsWith('_revenue') && key !== 'ecom_revenue' && key !== 'pos_revenue' && key !== 'subscription_revenue') {
          processors.add(key.replace('_revenue', ''));
        }
      });
    });
    return Array.from(processors);
  };
  
  // Processor colors
  const PROCESSOR_COLORS = {
    'stripe': '#635bff',
    'adyen': '#0abf53',
    'checkout': '#00d084',
    'worldpay': '#e91e63',
    'braintree': '#003087',
    'paypal': '#00457c',
    'test_proc': '#9e9e9e'
  };
  
  const getProcessorColor = (processor) => {
    return PROCESSOR_COLORS[processor.toLowerCase()] || 
           `hsl(${Math.abs(processor.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % 360}, 70%, 50%)`;
  };

  // Generate complete 12-month data with 0 for missing months
  const generateMonthlyData = () => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    
    // Only map data for the CURRENT calendar year. Backend now returns `year` on every
    // series, so entries from an adjacent year (e.g. Nov/Dec of last year that a rolling
    // window may still include) are excluded instead of bleeding into the same-named
    // month slot. monthly_revenue historically lacked `year`; treat a missing year as
    // the current year for backward-compatibility.
    const inYear = (item) => item.year === undefined || item.year === currentYear;
    
    // Create map of existing data by month NAME (backend returns month as string like "Jan", "Feb")
    const dataMap = {};
    monthlyRevenueData.forEach(item => {
      if (item.month && inYear(item)) {
        dataMap[item.month] = item;
      }
    });
    
    // Create map of channel data by month
    const channelDataMap = {};
    monthlyChannelData.forEach(item => {
      if (item.month && inYear(item)) {
        channelDataMap[item.month] = item;
      }
    });
    
    // Create map of processor data by month
    const processorDataMap = {};
    monthlyProcessorData.forEach(item => {
      if (item.month && inYear(item)) {
        processorDataMap[item.month] = item;
      }
    });
    
    console.log('[Monthly Revenue Chart] dataMap:', dataMap);
    console.log('[Monthly Revenue Chart] processorDataMap:', processorDataMap);
    
    return months.map((monthName, index) => {
      const existingData = dataMap[monthName];
      const channelData = channelDataMap[monthName];
      const processorData = processorDataMap[monthName];
      const isFutureMonth = index > currentMonth;
      
      const entry = {
        month: monthName,
        revenue: existingData?.revenue || 0,
        isFutureMonth,
        transactions: existingData?.transactions || 0,
        // Channel breakdown for stacked chart
        ecom_revenue: channelData?.ecom_revenue || 0,
        pos_revenue: channelData?.pos_revenue || 0,
        subscription_revenue: channelData?.subscription_revenue || 0,
        ecom_transactions: channelData?.ecom_transactions || 0,
        pos_transactions: channelData?.pos_transactions || 0,
        subscription_transactions: channelData?.subscription_transactions || 0
      };
      
      // Add processor data
      if (processorData) {
        Object.keys(processorData).forEach(key => {
          if (key.endsWith('_revenue') || key.endsWith('_transactions')) {
            entry[key] = processorData[key];
          }
        });
      }
      
      return entry;
    });
  };

  const monthlyData = generateMonthlyData();
  const processors = getProcessors();

  // Metric selection: Revenue ($) vs Transactions (count). The monthly data already
  // carries both `revenue`/`transactions` and per-channel/per-processor `_revenue`/
  // `_transactions` fields, so the toggle just switches which keys drive the chart.
  const isTxn = metric === 'transactions';
  const valueKey = isTxn ? 'transactions' : 'revenue';
  const metricSuffix = isTxn ? '_transactions' : '_revenue';
  const channelKeys = {
    ecom: `ecom${metricSuffix}`,
    pos: `pos${metricSuffix}`,
    subscription: `subscription${metricSuffix}`,
  };
  const formatMetric = (v) => isTxn ? (Number(v) || 0).toLocaleString() : formatCurrencyForOverview(v, currency);
  const maxValue = Math.max(...monthlyData.map(d => d[valueKey] || 0));
  const totalValue = monthlyData.reduce((sum, m) => sum + (m[valueKey] || 0), 0);
  const activeMonthsCount = monthlyData.filter(m => !m.isFutureMonth && (m[valueKey] || 0) > 0).length;
  const avgValue = activeMonthsCount > 0 ? totalValue / activeMonthsCount : 0;

  return (
    <Card className="col-span-2 border-0 shadow-lg bg-white">
      <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-gray-800">
            <div className="p-2 bg-blue-500 rounded-lg text-white">
              <TrendingUp className="h-5 w-5" />
            </div>
            {isTxn ? 'Monthly Transactions' : 'Monthly Revenue'}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Metric toggle: Revenue vs Transactions */}
            <div className="flex bg-gray-100 rounded-lg p-1" data-testid="monthly-metric-toggle">
              <button
                onClick={() => setMetric('revenue')}
                data-testid="monthly-metric-revenue"
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  metric === 'revenue'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Revenue
              </button>
              <button
                onClick={() => setMetric('transactions')}
                data-testid="monthly-metric-transactions"
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  metric === 'transactions'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Transactions
              </button>
            </div>
            {/* Chart mode toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setChartMode('total')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  chartMode === 'total' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Total
              </button>
              <button
                onClick={() => setChartMode('channel')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  chartMode === 'channel' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Channel
              </button>
              <button
                onClick={() => setChartMode('processor')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  chartMode === 'processor' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Processor
              </button>
            </div>
            {loading && (
              <div className="flex items-center text-sm text-muted-foreground">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                Loading...
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="h-80">
          {unavailable ? (
            <div className="h-full flex items-center justify-center">
              <DataUnavailable onRetry={() => setReloadKey(k => k + 1)} testId="monthly-revenue-unavailable" />
            </div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={(value) => isTxn
                  ? (value >= 1000 ? `${(value / 1000).toFixed(1)}K` : `${value}`)
                  : `$${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value, name) => {
                  const base = name.replace(/_(revenue|transactions)$/, '');
                  const labels = {
                    'ecom': 'Ecom',
                    'pos': 'POS',
                    'subscription': 'Subscription',
                    'revenue': 'Total Revenue',
                    'transactions': 'Total Transactions'
                  };
                  const displayName = labels[base] || base.toUpperCase();
                  return [formatMetric(value), displayName];
                }}
                labelStyle={{ color: '#374151', fontWeight: 'bold' }}
              />
              {chartMode === 'channel' ? (
                <>
                  <Bar 
                    dataKey={channelKeys.ecom} 
                    stackId="a"
                    fill="#3b82f6"
                    radius={[0, 0, 0, 0]}
                    name={channelKeys.ecom}
                  />
                  <Bar 
                    dataKey={channelKeys.pos} 
                    stackId="a"
                    fill="#10b981"
                    radius={[0, 0, 0, 0]}
                    name={channelKeys.pos}
                  />
                  <Bar 
                    dataKey={channelKeys.subscription} 
                    stackId="a"
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                    name={channelKeys.subscription}
                  />
                </>
              ) : chartMode === 'processor' ? (
                <>
                  {processors.map((proc, idx) => (
                    <Bar 
                      key={proc}
                      dataKey={`${proc}${metricSuffix}`}
                      stackId="b"
                      fill={getProcessorColor(proc)}
                      radius={idx === processors.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      name={`${proc}${metricSuffix}`}
                    />
                  ))}
                </>
              ) : (
                <Bar 
                  dataKey={valueKey} 
                  radius={[4, 4, 0, 0]}
                  name={valueKey}
                >
                  {monthlyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.isFutureMonth ? '#e5e7eb' : '#3b82f6'} 
                    />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
        
        {/* Channel Legend */}
        {chartMode === 'channel' && (
          <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span className="text-xs text-gray-600">Ecom (Web/Link)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500"></div>
              <span className="text-xs text-gray-600">POS</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500"></div>
              <span className="text-xs text-gray-600">Subscription</span>
            </div>
          </div>
        )}
        
        {/* Processor Legend */}
        {chartMode === 'processor' && processors.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
            {processors.map(proc => (
              <div key={proc} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded" 
                  style={{ backgroundColor: getProcessorColor(proc) }}
                ></div>
                <span className="text-xs text-gray-600">{proc.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Monthly Summary Stats */}
        <div className={`mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 ${chartMode === 'total' ? 'border-t border-gray-100' : ''}`}>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate" data-testid="monthly-total-value">
              {formatMetric(totalValue)}
            </p>
            <p className="text-sm text-gray-600 font-medium">{isTxn ? 'Total Transactions' : 'Total Revenue'}</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {formatMetric(maxValue)}
            </p>
            <p className="text-sm text-gray-600 font-medium">Highest Month</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {formatMetric(avgValue)}
            </p>
            <p className="text-sm text-gray-600 font-medium">Avg per Month</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PaymentMethodsChart = ({ data, currency, paymentMethodLogos = {} }) => {
  // Calculate total revenue for percentage calculation
  const totalRevenue = data.reduce((sum, method) => sum + (method.revenue || method.total_amount || 0), 0);
  
  // Helper function to get payment method icon/logo
  const getPaymentMethodIcon = (methodName, index) => {
    const methodLower = methodName?.toLowerCase();
    
    // Check if we have a logo from settings table
    if (paymentMethodLogos[methodLower]) {
      return (
        <img 
          src={paymentMethodLogos[methodLower]} 
          alt={methodName}
          className="h-8 w-auto object-contain"
          style={{ maxWidth: '50px', minWidth: '32px' }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
          }}
        />
      );
    }
    
    // Fallback colored dot for methods without logos
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return (
      <div className={`w-8 h-8 rounded-lg ${colors[index % colors.length]} flex items-center justify-center`}>
        <CreditCard className="h-4 w-4 text-white" />
      </div>
    );
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-blue-500 rounded-lg text-white">
            <CreditCard className="h-6 w-6" />
          </div>
          Payment Methods
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {data.filter(hasBreakdownValue).slice(0, 8).map((method, index) => {
            const revenuePercentage = totalRevenue > 0 ? ((method.revenue || method.total_amount || 0) / totalRevenue * 100).toFixed(1) : 0;
            return (
              <div key={`${method.method}-${method.processor}`} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  {getPaymentMethodIcon(method.method, index)}
                  <span className="text-base font-semibold text-gray-900">{method.method}</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{formatCurrencyForOverview(method.revenue || method.total_amount || 0, currency)}</div>
                  <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(method.approved_count || 0, currency)} • {revenuePercentage}%</div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const ProcessorChart = ({ data, currency }) => {
  // Calculate total revenue for percentage calculation
  const totalRevenue = data ? data.reduce((sum, processor) => sum + (processor.revenue || 0), 0) : 0;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-orange-500 rounded-lg text-white">
            <Activity className="h-6 w-6" />
          </div>
          Payment Processors
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {data && data.length > 0 ? (
            data.filter(hasBreakdownValue).slice(0, 4).map((processor, index) => {
              const revenuePercentage = totalRevenue > 0 ? ((processor.revenue || 0) / totalRevenue * 100).toFixed(1) : 0;
              return (
                <div key={processor.processor || processor.name} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      index === 0 ? 'bg-indigo-500' : 
                      index === 1 ? 'bg-pink-500' : 
                      index === 2 ? 'bg-orange-500' : 'bg-teal-500'
                    }`} />
                    <div>
                      <span className="text-base font-semibold text-gray-900">{processor.processor || processor.name}</span>
                      <div className="text-sm text-muted-foreground font-medium">{(processor.success_rate || 0).toFixed(1)}% success rate</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{formatCurrencyForOverview(processor.revenue || 0, currency)}</div>
                    <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(processor.approved_count || 0)} • {revenuePercentage}%</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                <Activity className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">No processor data available for the selected filters</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CountryChart = ({ data, currency }) => {
  // Calculate total revenue for percentage calculation
  const totalRevenue = data.reduce((sum, country) => sum + (country.revenue || country.total_amount || 0), 0);
  
  // Get country flag emoji with fallback icons
  const getCountryFlag = (countryCode) => {
    if (!countryCode || typeof countryCode !== 'string' || countryCode.length !== 2) {
      return '🌍';
    }
    
    // Static flag mapping as primary method
    const staticFlags = {
      'US': '🇺🇸', 'CR': '🇨🇷', 'HN': '🇭🇳', 'CO': '🇨🇴', 'SV': '🇸🇻',
      'CA': '🇨🇦', 'GB': '🇬🇧', 'PA': '🇵🇦', 'MX': '🇲🇽', 'CH': '🇨🇭',
      'DE': '🇩🇪', 'FR': '🇫🇷', 'ES': '🇪🇸', 'IT': '🇮🇹', 'BR': '🇧🇷',
      'AR': '🇦🇷', 'CL': '🇨🇱', 'PE': '🇵🇪', 'AU': '🇦🇺', 'NZ': '🇳🇿'
    };
    
    const code = countryCode.toUpperCase();
    
    // Try static mapping first
    if (staticFlags[code]) {
      console.log(`Static flag for ${code}:`, staticFlags[code]);
      return staticFlags[code];
    }
    
    // Fallback to Unicode conversion
    try {
      const flagEmoji = String.fromCodePoint(
        ...[...code].map(char => 0x1F1E6 + char.charCodeAt(0) - 65)
      );
      console.log(`Unicode flag for ${code}:`, flagEmoji);
      return flagEmoji;
    } catch (error) {
      console.log(`Flag generation failed for ${code}:`, error);
      return '🌍';
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-green-500 rounded-lg text-white">
            <Globe className="h-6 w-6" />
          </div>
          Countries
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {data.filter(hasBreakdownValue).slice(0, 4).map((country, index) => {
            const revenuePercentage = totalRevenue > 0 ? ((country.revenue || country.total_amount || 0) / totalRevenue * 100).toFixed(1) : 0;
            return (
              <div key={country.country} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <span 
                    className={`fi fi-${country.country?.toLowerCase()} text-3xl`}
                    style={{ fontSize: '2rem', lineHeight: '1' }}
                    title={`Flag for ${country.country}`}
                  ></span>
                  <div>
                    <span className="text-base font-semibold text-gray-900">{getCountryName(country.country)}</span>
                    <div className="text-sm text-muted-foreground font-medium flex items-center gap-1">
                      <span 
                        style={{ fontSize: '1rem', fontFamily: 'Arial, sans-serif' }}
                        title={`Flag for ${country.country}`}
                      >
                        {(() => {
                          if (country.country === 'US') return '🇺🇸';
                          if (country.country === 'CR') return '🇨🇷';
                          if (country.country === 'HN') return '🇭🇳';
                          if (country.country === 'CO') return '🇨🇴';
                          if (country.country === 'SV') return '🇸🇻';
                          return getCountryFlag(country.country);
                        })()}
                      </span>
                      <span>{country.currency || getCountryName(country.country)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{formatCurrencyForOverview(country.revenue || country.total_amount || 0, currency)}</div>
                  <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(country.count, currency)} • {revenuePercentage}%</div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const CurrencyBreakdown = ({ currencies, currency }) => {
  // Calculate total revenue for percentage calculation
  const totalRevenue = currencies.reduce((sum, currencyItem) => sum + (currencyItem.revenue || currencyItem.total_amount || 0), 0);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-purple-500 rounded-lg text-white">
            <DollarSign className="h-6 w-6" />
          </div>
          Currency Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {currencies.filter(hasBreakdownValue).slice(0, 6).map((currencyItem, index) => {
            const revenuePercentage = totalRevenue > 0 ? ((currencyItem.revenue || currencyItem.total_amount || 0) / totalRevenue * 100).toFixed(1) : 0;
            return (
              <div key={currencyItem.currency} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${
                    index === 0 ? 'bg-emerald-500' : 
                    index === 1 ? 'bg-sky-500' : 
                    index === 2 ? 'bg-violet-500' :
                    index === 3 ? 'bg-rose-500' :
                    index === 4 ? 'bg-amber-500' : 'bg-slate-500'
                  }`} />
                  <span className="text-base font-bold font-mono text-gray-900">{currencyItem.currency}</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{formatCurrencyForOverview(currencyItem.revenue || currencyItem.total_amount || 0, currencyItem.currency)}</div>
                  <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(currencyItem.count, currencyItem.currency)} • {revenuePercentage}%</div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const TransactionTypeChart = ({ data, currency }) => {
  const totalRevenue = data ? data.reduce((sum, item) => sum + Math.abs(item.revenue || 0), 0) : 0;
  
  // Color mapping for transaction types
  const typeColors = {
    'sale': 'bg-green-500',
    'authorization': 'bg-blue-500',
    'capture': 'bg-indigo-500',
    'void': 'bg-red-500',
    'refund': 'bg-orange-500',
    'chargeback': 'bg-rose-600',
    'recurring charge': 'bg-purple-500',
    'subscription': 'bg-cyan-500',
    'sale adjustment': 'bg-amber-500'
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-indigo-500 rounded-lg text-white">
            <BarChart3 className="h-6 w-6" />
          </div>
          Transaction Types
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {data && data.length > 0 ? (
            data.filter(hasBreakdownValue).slice(0, 6).map((item, index) => {
              const revenuePercentage = totalRevenue > 0 ? (Math.abs(item.revenue || 0) / totalRevenue * 100).toFixed(1) : 0;
              const isNegative = item.revenue < 0;
              return (
                <div key={item.type} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${typeColors[item.type?.toLowerCase()] || 'bg-gray-500'}`} />
                    <span className="text-base font-semibold text-gray-900 capitalize">{item.type}</span>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${isNegative ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatCurrencyForOverview(item.revenue || 0, currency)}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(item.count)} • {revenuePercentage}%</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">No transaction type data available</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ChannelChart = ({ data, currency }) => {
  const totalRevenue = data ? data.reduce((sum, item) => sum + (item.revenue || 0), 0) : 0;
  
  // Color mapping for channels
  const channelColors = {
    'web': 'bg-blue-500',
    'mobile_app': 'bg-green-500',
    'mobile_web': 'bg-teal-500',
    'api': 'bg-purple-500',
    'pos': 'bg-orange-500',
    'recurring': 'bg-indigo-500',
    'manual_link': 'bg-pink-500',
    'email_link': 'bg-cyan-500',
    'file_link': 'bg-amber-500',
    'other': 'bg-gray-500'
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 bg-teal-500 rounded-lg text-white">
            <Activity className="h-6 w-6" />
          </div>
          Channels
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {data && data.length > 0 ? (
            data.filter(hasBreakdownValue).slice(0, 6).map((item, index) => {
              const revenuePercentage = totalRevenue > 0 ? ((item.revenue || 0) / totalRevenue * 100).toFixed(1) : 0;
              return (
                <div key={item.channel} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${channelColors[item.channel?.toLowerCase()] || 'bg-gray-500'}`} />
                    <span className="text-base font-semibold text-gray-900 capitalize">{item.channel?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{formatCurrencyForOverview(item.revenue || 0, currency)}</div>
                    <div className="text-sm text-muted-foreground font-medium">{formatNumberForOverview(item.count)} • {revenuePercentage}%</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">No channel data available</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Constants moved outside component to prevent recreation on each render
const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const PERIOD_OPTIONS = [
  { value: 'thismonth', label: 'This Month' },
  { value: 'lastmonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Month/Year' }
];

// Generate year options once
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const RevenueByProcessorChart = React.memo(({ selectedPlatform: propPlatform, currency, merchantId, backendUrl, mainFilterPeriod, mainFilterMonth, mainFilterYear, channel }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(mainFilterPeriod || 'thismonth');
  const [selectedYear, setSelectedYear] = useState(mainFilterYear || CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState(mainFilterMonth || new Date().getMonth() + 1);
  const [dailyRevenueData, setDailyRevenueData] = useState([]);
  const [lastMonthData, setLastMonthData] = useState([]);
  const [compareWithLastMonth, setCompareWithLastMonth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  
  // Sync with main filter changes
  useEffect(() => {
    if (mainFilterPeriod) {
      setSelectedPeriod(mainFilterPeriod);
      if (mainFilterPeriod === 'custom' && mainFilterMonth && mainFilterYear) {
        setSelectedMonth(mainFilterMonth);
        setSelectedYear(mainFilterYear);
      }
    }
  }, [mainFilterPeriod, mainFilterMonth, mainFilterYear]);
  
  const { token } = useAuth();
  const { selectedPlatform: contextPlatform } = usePlatform();
  
  const actualPlatform = propPlatform || contextPlatform || 'plt_default';
  const API = `${backendUrl}/api`;

  // Fetch daily revenue data
  const fetchDailyRevenueData = useCallback(async (period, year, month, fetchComparison = false) => {
    if (!token || !actualPlatform) return;
    
    setLoading(true);
    setUnavailable(false);
    
    try {
      let startDate, endDate;
      const today = new Date();
      
      if (period === 'thismonth') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      } else if (period === 'lastmonth') {
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      } else {
        // Custom month/year
        startDate = new Date(year, month - 1, 1);
        endDate = new Date(year, month, 0);
      }

      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01`;

      const params = new URLSearchParams({
        days: days.toString(),
        platform_id: actualPlatform,
        start_date: startDateStr
      });

      if (currency && currency !== 'all') {
        params.append('currency', currency);
      }
      if (merchantId && merchantId !== 'all') {
        params.append('merchant_id', merchantId);
      }
      appendChannelParams(params, channel);

      const response = await axios.get(`${API}/analytics/daily-revenue?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDailyRevenueData(response.data.daily_revenue || []);
      
      // Fetch last month's data for comparison if enabled
      if (fetchComparison) {
        let lastMonthStart;
        if (period === 'thismonth') {
          lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        } else if (period === 'lastmonth') {
          lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        } else {
          // For custom, get previous month
          lastMonthStart = new Date(year, month - 2, 1);
        }
        const lastMonthEnd = new Date(lastMonthStart.getFullYear(), lastMonthStart.getMonth() + 1, 0);
        const lastMonthDays = Math.ceil((lastMonthEnd - lastMonthStart) / (1000 * 60 * 60 * 24)) + 1;
        const lastMonthStartStr = `${lastMonthStart.getFullYear()}-${String(lastMonthStart.getMonth() + 1).padStart(2, '0')}-01`;
        
        const lastMonthParams = new URLSearchParams({
          days: lastMonthDays.toString(),
          platform_id: actualPlatform,
          start_date: lastMonthStartStr
        });
        
        if (currency && currency !== 'all') {
          lastMonthParams.append('currency', currency);
        }
        if (merchantId && merchantId !== 'all') {
          lastMonthParams.append('merchant_id', merchantId);
        }
        appendChannelParams(lastMonthParams, channel);
        
        const lastMonthResponse = await axios.get(`${API}/analytics/daily-revenue?${lastMonthParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setLastMonthData(lastMonthResponse.data.daily_revenue || []);
      } else {
        setLastMonthData([]);
      }
    } catch (err) {
      console.error('Error fetching daily revenue data:', err);
      if (isAnalyticsUnavailable(err)) setUnavailable(true);
      setDailyRevenueData([]);
      setLastMonthData([]);
    } finally {
      setLoading(false);
    }
  }, [token, actualPlatform, currency, merchantId, API, Array.isArray(channel) ? channel.join(',') : channel]);

  // Single source of truth for fetching: runs on mount and whenever the resolved
  // selection or filters change. Covers the chart's own dropdowns AND syncing from
  // the main dashboard filter, always using the CURRENT selected values (fixes the
  // stale-closure fetch that left e.g. a chosen custom month showing empty).
  useEffect(() => {
    if (!token || !actualPlatform) return;
    fetchDailyRevenueData(selectedPeriod, selectedYear, selectedMonth, compareWithLastMonth);
  }, [token, actualPlatform, selectedPeriod, selectedYear, selectedMonth, compareWithLastMonth, fetchDailyRevenueData]);

  // Handlers just update state; the effect above performs the fetch with the
  // resolved values (avoids stale-closure fetches).
  const handlePeriodChange = (newPeriod) => setSelectedPeriod(newPeriod);

  const handleYearChange = (newYear) => setSelectedYear(parseInt(newYear));

  const handleMonthChange = (newMonth) => setSelectedMonth(parseInt(newMonth));

  // Prepare chart data with comparison (current month from day 1, last month starts at current month's first Monday)
  const chartData = useMemo(() => {
    if (!dailyRevenueData || dailyRevenueData.length === 0) return [];

    // Determine the calendar month being shown — DON'T derive from
    // dailyRevenueData[0].date because when a merchant filter is applied
    // the API returns sparse data that may start mid-month.
    const today = new Date();
    let refYear, refMonthIdx; // refMonthIdx is 0-based JS month
    if (selectedPeriod === 'thismonth') {
      refYear = today.getFullYear();
      refMonthIdx = today.getMonth();
    } else if (selectedPeriod === 'lastmonth') {
      const lastMonthRef = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      refYear = lastMonthRef.getFullYear();
      refMonthIdx = lastMonthRef.getMonth();
    } else {
      // custom
      refYear = selectedYear;
      refMonthIdx = selectedMonth - 1; // selectedMonth is 1-based
    }
    const totalDaysInMonth = new Date(refYear, refMonthIdx + 1, 0).getDate();

    // Build day-of-month → entry maps for fast lookup (works whether data is
    // contiguous or sparse, e.g. when merchant filter hides days with no txns).
    const currentByDay = new Map();
    dailyRevenueData.forEach((d) => {
      if (!d?.date) return;
      const dt = new Date(d.date);
      currentByDay.set(dt.getUTCDate(), d);
    });

    // Comparison mode: weekday-aligned.
    // Day 1 of current month maps to the FIRST occurrence of the same weekday
    // in last month. Example: if May 1 is Friday, it maps to the first Friday
    // of April (April 3). Day 2 → April 4, Day 3 → April 5, and so on.
    if (compareWithLastMonth && lastMonthData.length > 0) {
      // Compute alignment offset (in days) using the calendar — independent of
      // whether the dataset is sparse.
      const firstOfRefMonth = new Date(refYear, refMonthIdx, 1);
      const targetWeekday = firstOfRefMonth.getDay();

      const lastMonthRefYear = refMonthIdx === 0 ? refYear - 1 : refYear;
      const lastMonthRefIdx = refMonthIdx === 0 ? 11 : refMonthIdx - 1;
      const lastMonthLastDay = new Date(lastMonthRefYear, lastMonthRefIdx + 1, 0).getDate();

      let lastMonthStartDay = 1; // day-of-last-month that aligns with day 1 of current
      for (let d = 1; d <= lastMonthLastDay; d++) {
        if (new Date(lastMonthRefYear, lastMonthRefIdx, d).getDay() === targetWeekday) {
          lastMonthStartDay = d;
          break;
        }
      }

      const lastMonthByDay = new Map();
      lastMonthData.forEach((d) => {
        if (!d?.date) return;
        const dt = new Date(d.date);
        lastMonthByDay.set(dt.getUTCDate(), d);
      });

      return Array.from({ length: totalDaysInMonth }, (_, i) => {
        const day = i + 1;
        const currentData = currentByDay.get(day);
        const lastMonthLookupDay = lastMonthStartDay + i;
        const lastMonthDayData = lastMonthLookupDay <= lastMonthLastDay
          ? (lastMonthByDay.get(lastMonthLookupDay) || null)
          : null;
        const lastMonthDateStr = lastMonthDayData?.date
          || (lastMonthLookupDay <= lastMonthLastDay
            ? `${lastMonthRefYear}-${String(lastMonthRefIdx + 1).padStart(2, '0')}-${String(lastMonthLookupDay).padStart(2, '0')}`
            : '');
        return {
          day: day.toString(),
          dayLabel: `Day ${day}`,
          date: currentData?.date || `${refYear}-${String(refMonthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
          revenue: currentData?.revenue || null,
          transactions: currentData?.transactions || 0,
          lastMonthRevenue: lastMonthDayData?.revenue || null,
          lastMonthTransactions: lastMonthDayData?.transactions || 0,
          lastMonthDate: lastMonthDateStr,
        };
      });
    }

    // No comparison - show all days of current month
    return Array.from({ length: totalDaysInMonth }, (_, i) => {
      const day = i + 1;
      const dayData = currentByDay.get(day);
      return {
        day: day.toString(),
        dayLabel: `Day ${day}`,
        date: dayData?.date || `${refYear}-${String(refMonthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        revenue: dayData?.revenue || null,
        transactions: dayData?.transactions || 0,
        lastMonthRevenue: null,
        lastMonthTransactions: 0,
        lastMonthDate: '',
      };
    });
  }, [dailyRevenueData, lastMonthData, compareWithLastMonth, selectedPeriod, selectedYear, selectedMonth]);
  
  // Get previous month name for legend
  const getPreviousMonthName = () => {
    const today = new Date();
    let prevMonth;
    if (selectedPeriod === 'thismonth') {
      prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    } else if (selectedPeriod === 'lastmonth') {
      prevMonth = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    } else {
      prevMonth = new Date(selectedYear, selectedMonth - 2, 1);
    }
    return prevMonth.toLocaleString('default', { month: 'short', year: 'numeric' });
  };

  const getPeriodTitle = () => {
    if (selectedPeriod === 'thismonth') return 'This Month';
    if (selectedPeriod === 'lastmonth') return 'Last Month';
    const monthName = MONTH_OPTIONS.find(m => m.value === selectedMonth)?.label || '';
    return `${monthName} ${selectedYear}`;
  };

  return (
    <Card className="col-span-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Daily Revenue
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedPeriod === 'custom' && (
              <>
                <Select value={selectedMonth.toString()} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
            
            {/* Compare with Last Month Toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer ml-2">
              <input
                type="checkbox"
                checked={compareWithLastMonth}
                onChange={(e) => setCompareWithLastMonth(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="compare-last-month-toggle"
              />
              <span className="text-muted-foreground whitespace-nowrap">Compare with last month</span>
            </label>

            {loading && (
              <div className="flex items-center text-sm text-muted-foreground">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                Loading...
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {unavailable ? (
          <div className="h-64 w-full flex items-center justify-center">
            <DataUnavailable onRetry={() => fetchDailyRevenueData(selectedPeriod, selectedYear, selectedMonth, compareWithLastMonth)} testId="daily-revenue-unavailable" />
          </div>
        ) : loading ? (
          <div className="h-64 w-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading processor data...</p>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-64 w-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-muted-foreground mb-2">📊</div>
              <p className="text-sm text-muted-foreground">No revenue data available for {getPeriodTitle()}</p>
              <p className="text-xs text-muted-foreground mt-1">Try selecting a different time period</p>
            </div>
          </div>
        ) : (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%" debounce={0}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#666' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#666' }}
                  tickFormatter={(value) => formatCurrencyForOverview(value, currency)}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value, name, item) => {
                    const row = item?.payload || {};
                    if (name === 'revenue') {
                      const d = row.date ? new Date(row.date) : null;
                      const label = d ? ` (${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})` : '';
                      return [formatCurrencyForOverview(value, currency), `${getPeriodTitle()}${label}`];
                    }
                    const d = row.lastMonthDate ? new Date(row.lastMonthDate) : null;
                    const label = d ? ` (${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})` : '';
                    return [formatCurrencyForOverview(value, currency), `${getPreviousMonthName()}${label}`];
                  }}
                  labelFormatter={(label) => `Day ${label}`}
                />
                {compareWithLastMonth && (
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={(value) => value === 'revenue' ? getPeriodTitle() : getPreviousMonthName()}
                  />
                )}
                <Line 
                  type="monotone" 
                  dataKey="revenue"
                  name="revenue"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: 'white' }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {compareWithLastMonth && lastMonthData.length > 0 && (
                  <Line 
                    type="monotone" 
                    dataKey="lastMonthRevenue"
                    name="lastMonthRevenue"
                    stroke="#9ca3af"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#9ca3af', strokeWidth: 1, r: 3 }}
                    activeDot={{ r: 5, stroke: '#9ca3af', strokeWidth: 2, fill: 'white' }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        
        {!loading && chartData.length > 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-muted-foreground">
              {getPeriodTitle()} Revenue: {formatCurrencyForOverview(chartData.reduce((sum, day) => sum + (day.revenue || 0), 0), currency)} 
              • Transactions: {formatNumberForOverview(chartData.reduce((sum, day) => sum + (day.transactions || 0), 0))}
              {compareWithLastMonth && lastMonthData.length > 0 && (
                <>
                  {' | '}{getPreviousMonthName()}: {formatCurrencyForOverview(chartData.reduce((sum, day) => sum + (day.lastMonthRevenue || 0), 0), currency)}
                </>
              )}
            </p>
            {compareWithLastMonth && lastMonthData.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {(() => {
                  // Compute alignment from the calendar (not from data, which may be sparse
                  // when a merchant filter is applied).
                  const today = new Date();
                  let refYear, refMonthIdx;
                  if (selectedPeriod === 'thismonth') {
                    refYear = today.getFullYear();
                    refMonthIdx = today.getMonth();
                  } else if (selectedPeriod === 'lastmonth') {
                    const r = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    refYear = r.getFullYear();
                    refMonthIdx = r.getMonth();
                  } else {
                    refYear = selectedYear;
                    refMonthIdx = selectedMonth - 1;
                  }
                  const firstDay = new Date(refYear, refMonthIdx, 1);
                  const targetWeekday = firstDay.getDay();
                  const lastMonthRefYear = refMonthIdx === 0 ? refYear - 1 : refYear;
                  const lastMonthRefIdx = refMonthIdx === 0 ? 11 : refMonthIdx - 1;
                  const lastMonthLastDay = new Date(lastMonthRefYear, lastMonthRefIdx + 1, 0).getDate();
                  let alignedDate = null;
                  for (let d = 1; d <= lastMonthLastDay; d++) {
                    const dt = new Date(lastMonthRefYear, lastMonthRefIdx, d);
                    if (dt.getDay() === targetWeekday) { alignedDate = dt; break; }
                  }
                  if (!alignedDate) return '';
                  const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  return `Weekday-aligned comparison: ${fmt(firstDay)} ↔ ${fmt(alignedDate)}`;
                })()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// Display name for debugging
RevenueByProcessorChart.displayName = 'RevenueByProcessorChart';

const HourlyRevenueChart = ({ data, token, selectedPlatform, customPeriod, customMonth, customYear, currency, merchantId, mainFilterPeriod, backendUrl, channel }) => {
  // Initialize date components first with today's date
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // Month is 0-indexed
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hourlyData, setHourlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  
  // Define API URL inside component
  const API = `${backendUrl}/api`;

  // Sync with main filter's period changes
  useEffect(() => {
    const now = new Date();
    let newYear, newMonth, newDay;
    
    if (customPeriod && customMonth && customYear) {
      // Custom period: use the specified month/year, day 1
      newYear = customYear;
      newMonth = customMonth;
      newDay = 1;
    } else if (mainFilterPeriod === 'today') {
      // Today: set to current date
      newYear = now.getFullYear();
      newMonth = now.getMonth() + 1;
      newDay = now.getDate();
    } else if (mainFilterPeriod === 'last7days' || mainFilterPeriod === 'thismonth' || mainFilterPeriod === 'thisyear') {
      // For these periods, show today's hourly data
      newYear = now.getFullYear();
      newMonth = now.getMonth() + 1;
      newDay = now.getDate();
    } else {
      // Default to today
      newYear = now.getFullYear();
      newMonth = now.getMonth() + 1;
      newDay = now.getDate();
    }
    
    // Update all state at once
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
    setSelectedDay(newDay);
    setSelectedDate(new Date(newYear, newMonth - 1, newDay));
  }, [customPeriod, customMonth, customYear, mainFilterPeriod]);

  // Keep selectedDate in sync with individual date fields
  useEffect(() => {
    if (selectedYear && selectedMonth && selectedDay) {
      setSelectedDate(new Date(selectedYear, selectedMonth - 1, selectedDay));
    }
  }, [selectedYear, selectedMonth, selectedDay]);

  // Generate complete hourly data for selected date (always 24 hours)
  const generateHourlyData = useCallback((apiData, targetDate) => {
    const completeData = [];
    // Always show all 24 hours regardless of whether it's today or not
    const maxHour = 23;
    
    // Create data for all 24 hours (0-23)
    for (let hour = 0; hour <= maxHour; hour++) {
      const hourData = apiData.find(item => parseInt(item.hour) === hour);
      const hourLabel = hour === 0 ? '12 AM' : 
                       hour < 12 ? `${hour} AM` : 
                       hour === 12 ? '12 PM' : 
                       `${hour - 12} PM`;
      
      completeData.push({
        hour: hour.toString(),
        hourLabel,
        revenue: hourData ? hourData.revenue : 0,
        transaction_count: hourData ? hourData.transaction_count : 0
      });
    }
    
    return completeData;
  }, []);

  // Fetch hourly data for selected date
  const fetchHourlyData = useCallback(async () => {
    if (!token || !selectedPlatform) {
      console.log('Missing token or platform:', { token: !!token, selectedPlatform });
      return;
    }

    setLoading(true);
    setUnavailable(false);
    try {
      const year = selectedYear;
      const month = String(selectedMonth).padStart(2, '0');
      const day = String(selectedDay).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      console.log('Fetching hourly data for:', dateStr, 'platform:', selectedPlatform);
      
      const params = new URLSearchParams({
        platform_id: selectedPlatform,
        date: dateStr
      });

      // Add currency parameter for correct totalization
      if (currency && currency !== 'all') {
        params.append('currency', currency);
      }

      // Add merchant_id parameter
      if (merchantId && merchantId !== 'all') {
        params.append('merchant_id', merchantId);
      }

      appendChannelParams(params, channel);

      const url = `${API}/analytics/hourly?${params}`;
      console.log('API URL:', url);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('API response:', response.data);
      const hourlyData = response.data.hourly_revenue || [];
      console.log('Setting hourly data:', hourlyData.length, 'data points');
      
      setHourlyData(hourlyData);
    } catch (err) {
      console.error('Error fetching hourly data:', err);
      if (isAnalyticsUnavailable(err)) setUnavailable(true);
      setHourlyData([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedPlatform, selectedYear, selectedMonth, selectedDay, currency, merchantId, backendUrl, Array.isArray(channel) ? channel.join(',') : channel]);

  // Fetch data when date, currency, or platform changes - always fetch fresh data
  useEffect(() => {
    console.log('Fetch useEffect triggered:', { selectedYear, selectedMonth, selectedDay, currency, token: !!token, selectedPlatform });
    if (token && selectedPlatform && selectedYear && selectedMonth && selectedDay) {
      console.log('Calling fetchHourlyData from useEffect');
      fetchHourlyData();
    }
  }, [selectedYear, selectedMonth, selectedDay, currency, token, selectedPlatform, fetchHourlyData]);

  const chartData = useMemo(() => {
    return generateHourlyData(hourlyData, selectedDate);
  }, [hourlyData, selectedDate, generateHourlyData]);

  const handleDateFieldChange = (field, value) => {
    const intValue = parseInt(value);
    
    if (field === 'year') {
      setSelectedYear(intValue);
    } else if (field === 'month') {
      setSelectedMonth(intValue);
    } else if (field === 'day') {
      setSelectedDay(intValue);
    }
    
    // The useEffect will automatically fetch data when state changes
    console.log('Date field changed:', field, 'to:', value);
  };

  // Generate options for dropdowns
  const generateYearOptions = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear - 5; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  const generateMonthOptions = () => {
    const months = [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];
    return months;
  };

  const generateDayOptions = () => {
    const days = [];
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const isToday = () => {
    const today = new Date();
    return selectedYear === today.getFullYear() && 
           selectedMonth === (today.getMonth() + 1) && 
           selectedDay === today.getDate();
  };

  return (
    <Card className="col-span-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Hourly Revenue {loading && <span className="text-sm text-blue-500">(Updating...)</span>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-muted-foreground">Date:</label>
            <div className="flex items-center gap-1">
              {/* Year Dropdown */}
              <select
                value={selectedYear}
                onChange={(e) => handleDateFieldChange('year', e.target.value)}
                disabled={loading}
                className={`px-2 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                  loading ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              
              {/* Month Dropdown */}
              <select
                value={selectedMonth}
                onChange={(e) => handleDateFieldChange('month', e.target.value)}
                disabled={loading}
                className={`px-2 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                  loading ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                {generateMonthOptions().map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
              
              {/* Day Dropdown */}
              <select
                value={selectedDay}
                onChange={(e) => handleDateFieldChange('day', e.target.value)}
                disabled={loading}
                className={`px-2 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                  loading ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                {generateDayOptions().map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => {
                console.log('Manual refresh clicked - fetching hourly data for:', {
                  year: selectedYear,
                  month: selectedMonth,
                  day: selectedDay,
                  platform: selectedPlatform
                });
                setLoading(true);
                fetchHourlyData();
              }}
              disabled={loading}
              className={`h-10 px-3 rounded-md flex items-center justify-center transition-colors ${
                loading 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
              }`}
              title="Refresh hourly data"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {isToday() ? 'Today' : `${generateMonthOptions().find(m => m.value === selectedMonth)?.label} ${selectedDay}, ${selectedYear}`}
        </p>
      </CardHeader>
      <CardContent>
        {unavailable ? (
          <div className="h-64 w-full flex items-center justify-center">
            <DataUnavailable onRetry={() => fetchHourlyData()} testId="hourly-revenue-unavailable" />
          </div>
        ) : loading ? (
          <div className="h-64 w-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="hourLabel"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#666' }}
                    interval={Math.max(1, Math.floor(chartData.length / 8))}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666' }}
                    tickFormatter={(value) => formatCurrencyForOverview(value, currency)}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    formatter={(value, name) => [
                      formatCurrencyForOverview(value, currency),
                      name === 'revenue' ? 'Revenue' : name
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: 'white' }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-center">
              {chartData.length === 0 || chartData.every(item => item.revenue === 0 && item.transaction_count === 0) ? (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    No transaction data available for {isToday() ? 'today' : 'the selected date'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Try selecting a different date or check if there are transactions for this platform
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {isToday() ? "Today's" : "Selected Day's"} Total: {formatCurrencyForOverview(chartData.reduce((sum, item) => sum + item.revenue, 0), currency)} 
                  • Transactions: {formatNumberForOverview(chartData.reduce((sum, item) => sum + item.transaction_count, 0))}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Memoize HourlyRevenueChart to prevent unnecessary re-renders
const MemoizedHourlyRevenueChart = React.memo(HourlyRevenueChart);

const TopMerchants = ({ merchants, currency }) => {
  // Get merchant's currency for formatting based on current filter
  const getMerchantCurrency = (merchant) => {
    // If "All Currencies" is selected, use USD formatting
    if (!currency || currency === 'all') {
      return 'USD';
    }
    // Otherwise, use the selected filter currency
    return currency;
  };

  return (
  <Card className="col-span-full border-0 shadow-lg bg-white">
    <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
      <CardTitle className="flex items-center gap-3 text-gray-800">
        <div className="p-2 bg-blue-500 rounded-lg text-white">
          <Store className="h-5 w-5" />
        </div>
        Top Merchants
      </CardTitle>
    </CardHeader>
    <CardContent className="p-6">
      {merchants.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
            <Store className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg">No merchant data available for the selected filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {merchants.map((merchant, index) => (
            <div key={merchant.merchant_id} className="flex flex-col xl:flex-row xl:items-center justify-between p-4 xl:p-5 rounded-xl border border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/30 transition-all duration-200 hover:shadow-md gap-3">
              <div className="flex items-center space-x-3 xl:space-x-4 min-w-0">
                <div className="flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs xl:text-sm font-bold shadow-md shrink-0">
                  {index + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900 text-base xl:text-lg leading-tight truncate">{merchant.merchant_name}</p>
                  <p className="text-xs xl:text-sm text-gray-600 capitalize font-medium">
                    {merchant.merchant_group.replace('_', ' ')}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 xl:flex xl:items-center gap-4 xl:gap-0 xl:space-x-8 text-left xl:text-right ml-11 xl:ml-0">
                {/* Markets - Leftmost column */}
                <div className="xl:min-w-[120px]">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Markets</p>
                  <div className="flex flex-wrap gap-1 xl:justify-end">
                    {merchant.countries.slice(0, 3).map((country) => (
                      <span key={country} className="text-xs bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium border border-blue-200">
                        {country}
                      </span>
                    ))}
                    {merchant.countries.length > 3 && (
                      <span className="text-xs bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium border border-gray-200">
                        +{merchant.countries.length - 3}
                      </span>
                    )}
                  </div>
                </div>
                {/* Avg Amount */}
                <div className="xl:min-w-[100px]">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Avg Amount</p>
                  <p className="text-base xl:text-xl font-bold text-gray-900 truncate">
                    {formatCurrencyForOverview(merchant.avg_transaction, getMerchantCurrency(merchant))}
                  </p>
                </div>
                {/* Transactions */}
                <div className="xl:min-w-[100px]">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Transactions</p>
                  <p className="text-base xl:text-xl font-bold text-gray-900 truncate">
                    {formatNumberForOverview(merchant.total_transactions)}
                  </p>
                </div>
                {/* Revenue - Rightmost column */}
                <div className="xl:min-w-[120px]">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Revenue</p>
                  <p className="text-base xl:text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent truncate">
                    {formatCurrencyForOverview(merchant.total_revenue, getMerchantCurrency(merchant))}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
};

const Overview = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [kpiMetrics, setKpiMetrics] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [topMerchants, setTopMerchants] = useState([]);
  const [yearlyDistinctCounts, setYearlyDistinctCounts] = useState({ countries_count: 0, currencies_count: 0, processors_count: 0 });
  const [lastTransactionTime, setLastTransactionTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsUnavailable, setAnalyticsUnavailable] = useState(false);
  const [filters, setFilters] = useState({
    country: 'all',
    currency: 'all',
    merchant_id: 'all',
    channel: [],
    period: 'thismonth',
    customMonth: new Date().getMonth() + 1,
    customYear: new Date().getFullYear()
  });
  const [availableFilters, setAvailableFilters] = useState({
    countries: [],
    currencies: [],
    merchants: []
  });
  
  // Payment method logos from settings table
  const [paymentMethodLogos, setPaymentMethodLogos] = useState({});

  // PDF export (client-side WYSIWYG capture of the Overview dashboard)
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const pdfContentRef = React.useRef(null);

  // Add authentication and platform context
  const { token, selectedPlatform, backendUrl } = useAuth();
  const { currentPlatform } = usePlatform();
  const { theme } = useTheme();
  const reference = useReference();
  
  // Use dynamic backend URL that works across dev/prod environments
  const API = `${backendUrl}/api`;

  useEffect(() => {
    if (token && selectedPlatform) {
      // Reset merchant filter when platform changes
      setFilters(prev => ({
        ...prev,
        merchant_id: 'all'
      }));
      fetchAvailableFilters();
      fetchYearlyDistinctCounts();
      fetchLastTransactionTime();
    }
  }, [token, selectedPlatform]);

  // Default the currency filter to the platform's configured default currency.
  // When "All Platforms" is selected, always reset to All Currencies (USD-equivalent).
  const platformDefaultCurrency = (selectedPlatform && selectedPlatform !== 'all')
    ? (currentPlatform?.default_currency || 'all')
    : 'all';
  useEffect(() => {
    if (!currentPlatform) return;
    setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformDefaultCurrency]);

  useEffect(() => {
    if (token && selectedPlatform) {
      // Debounce so the filter/platform contexts settling on first paint (merchant
      // reset + default-currency effects) collapse into ONE fetch instead of firing
      // the heavy /analytics/dashboard (and KPI) queries 3x. An AbortController cancels
      // any in-flight requests when the filters change again, so rapid filter changes
      // don't pile up overlapping heavy aggregations (and a slow stale response can't
      // overwrite the latest one).
      const controller = new AbortController();
      const t = setTimeout(() => {
        fetchKpis(controller.signal);
        fetchAnalyticsData(controller.signal);
        fetchTopMerchants(controller.signal);
      }, 150);
      return () => {
        clearTimeout(t);
        controller.abort();
      };
    }
  }, [filters, token, selectedPlatform]);

  // Fetch payment method logos from settings table
  useEffect(() => {
    const fetchPaymentMethodLogos = async () => {
      if (!token) return;
      
      try {
        // Use public endpoint that works for all authenticated users
        const response = await axios.get(`${API}/settings/payment-methods/logos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setPaymentMethodLogos(response.data.logos || {});
        console.log('[Overview] Payment method logos loaded:', response.data.logos);
      } catch (error) {
        console.error('[Overview] Error fetching payment method logos:', error);
      }
    };
    
    fetchPaymentMethodLogos();
  }, [token, API]);

  // Track the in-flight yearly-counts request so a stale response (from a
  // previous platform) can't overwrite the latest one when the user switches
  // platforms rapidly. Also used to drive the loading state.
  const yearlyCountsReqRef = React.useRef(0);

  const fetchYearlyDistinctCounts = async () => {
    if (!token || !selectedPlatform) return;

    // Reset counts to null so the UI shows "—" until fresh data arrives.
    setYearlyDistinctCounts({ countries_count: null, currencies_count: null, processors_count: null });

    const reqId = ++yearlyCountsReqRef.current;
    const requestedPlatform = selectedPlatform;

    try {
      // Use the auxiliary platform_stats collection — single document, all-time.
      console.log('[Dashboard] Fetching platform stats distinct counts:', { platform: requestedPlatform, reqId });
      const response = await axios.get(
        `${API}/platform-stats?platform_id=${encodeURIComponent(requestedPlatform)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Discard stale response if a newer request was started OR the platform
      // changed mid-flight.
      if (reqId !== yearlyCountsReqRef.current || requestedPlatform !== selectedPlatform) {
        console.log('[Dashboard] Stale platform-stats response discarded', { reqId, latest: yearlyCountsReqRef.current });
        return;
      }

      const dc = response.data?.distinct_counts || {};
      const counts = {
        countries_count: dc.countries ?? 0,
        currencies_count: dc.currencies ?? 0,
        processors_count: dc.processors ?? 0,
      };

      console.log('[Dashboard] Platform stats distinct counts received:', counts);
      setYearlyDistinctCounts(counts);
    } catch (err) {
      console.error('Error fetching platform stats distinct counts:', err);
      if (reqId === yearlyCountsReqRef.current) {
        // Reflect failure as 0 so the UI doesn't stay in "—" forever.
        setYearlyDistinctCounts({ countries_count: 0, currencies_count: 0, processors_count: 0 });
      }
    }
  };

  const fetchLastTransactionTime = async () => {
    if (!token || !selectedPlatform) return;
    
    try {
      // Use dedicated endpoint for getting last transaction time
      const params = new URLSearchParams({
        _t: Date.now().toString()  // Cache-busting timestamp for fresh data on reload
      });
      
      // Only add platform filter if not "all" platforms
      if (selectedPlatform && selectedPlatform !== 'all') {
        params.append('platform_id', selectedPlatform);
      }
      
      const response = await axios.get(`${API}/transactions/last?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.last_transaction_time) {
        setLastTransactionTime(response.data.last_transaction_time);
        console.log('[Dashboard] Last transaction created_at for platform', selectedPlatform, ':', response.data.last_transaction_time);
      } else {
        setLastTransactionTime(null);
      }
    } catch (err) {
      console.error('Error fetching last transaction time:', err);
      setLastTransactionTime(null);
    }
  };

  const fetchAvailableFilters = async () => {
    if (!token || !selectedPlatform) return;

    // Country list is data-driven (only countries present in this platform's data).
    // Currency comes from the DB Settings tables (ReferenceProvider). Merchants are
    // real entities, also data-driven.
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const currentYear = new Date().getFullYear();
      const countriesReq = axios.get(
        `${API}/filters/countries?platform_id=${selectedPlatform}&year=${currentYear}&_t=${Date.now()}`,
        { headers }
      );
      const merchantsReq = (selectedPlatform && selectedPlatform !== 'all')
        ? axios.get(`${API}/filters/merchants?platform_id=${selectedPlatform}&_t=${Date.now()}`, { headers })
        : Promise.resolve({ data: { merchants: [] } });

      const [countriesRes, merchantsRes] = await Promise.all([countriesReq, merchantsReq]);
      setAvailableFilters(prev => ({
        ...prev,
        countries: countriesRes.data.countries || [],
        merchants: merchantsRes.data.merchants || []
      }));
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  // Shared param builder for the dashboard + KPI endpoints (same date/filter logic).
  const buildDashboardParams = () => {
    const params = new URLSearchParams({ platform_id: selectedPlatform });
    if (filters.country && filters.country !== 'all') params.append('country', filters.country);
    if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency);
    if (filters.merchant_id && filters.merchant_id !== 'all') params.append('merchant_id', filters.merchant_id);
    appendChannelParams(params, filters.channel);

    const now = new Date();
    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00`;

    if (filters.period === 'today') {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      params.append('days', '1');
      params.append('start_date', formatDate(todayStart));
    } else if (filters.period === 'last7days') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      params.append('days', '7');
      params.append('start_date', formatDate(start));
    } else if (filters.period === 'thismonth') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      params.append('days', monthEnd.getDate().toString());
      params.append('start_date', formatDate(monthStart));
    } else if (filters.period === 'thisyear') {
      params.append('days', '365');
      params.append('start_date', `${now.getFullYear()}-01-01T00:00:00`);
    } else if (filters.period === 'custom') {
      const customStart = new Date(filters.customYear, filters.customMonth - 1, 1);
      const customEnd = new Date(filters.customYear, filters.customMonth, 0);
      params.append('days', customEnd.getDate().toString());
      params.append('start_date', formatDate(customStart));
    } else {
      params.append('days', '30');
    }
    return params;
  };

  // FAST KPI fetch (parallel mini-endpoint) — paints the top cards without waiting
  // for the heavy /analytics/dashboard mega-facet (charts/tables).
  const fetchKpis = async (signal) => {
    if (!token || !selectedPlatform) return;
    setKpiLoading(true);
    try {
      const params = buildDashboardParams();
      const response = await axios.get(`${API}/analytics/dashboard/kpis?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      setKpiMetrics(response.data?.metrics || null);
      setAnalyticsUnavailable(false);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      console.error('Error fetching KPI metrics:', err);
      if (isAnalyticsUnavailable(err)) setAnalyticsUnavailable(true);
      // Leave kpiMetrics as-is; full-dashboard metrics will serve as fallback.
    } finally {
      if (!signal?.aborted) setKpiLoading(false);
    }
  };

  const fetchAnalyticsData = async (signal) => {
    if (!token || !selectedPlatform) return;

    setLoading(true);
    setError(null);

    try {
      const params = buildDashboardParams();

      const response = await axios.get(`${API}/analytics/dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });

      setAnalyticsData(response.data);
      setError(null);
      setAnalyticsUnavailable(false);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      console.error('Error fetching analytics data:', err);
      if (isAnalyticsUnavailable(err)) {
        setAnalyticsUnavailable(true);
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const fetchTopMerchants = async (signal) => {
    if (!token || !selectedPlatform) return;
    
    try {
      const params = new URLSearchParams({
        platform_id: selectedPlatform,
        limit: '5'
      });
      
      // Apply date filters - use explicit start_date/end_date for all period types
      const now = new Date();
      const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00`;
      const formatDateEnd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T23:59:59`;
      
      if (filters.period === 'today') {
        // Today - full day from 00:00 to 23:59
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        params.append('start_date', formatDate(todayStart));
        params.append('end_date', formatDateEnd(todayEnd));
      } else if (filters.period === 'last7days') {
        // Last 7 days - from 7 days ago to end of today
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        params.append('start_date', formatDate(start));
        params.append('end_date', formatDateEnd(end));
      } else if (filters.period === 'thismonth') {
        // This month - full month from 1st to last day
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        params.append('start_date', formatDate(monthStart));
        params.append('end_date', formatDateEnd(monthEnd));
      } else if (filters.period === 'thisyear') {
        // This year - full year from Jan 1 to Dec 31
        params.append('start_date', `${now.getFullYear()}-01-01T00:00:00`);
        params.append('end_date', `${now.getFullYear()}-12-31T23:59:59`);
      } else if (filters.period === 'custom') {
        // Custom date range - full month
        const customStart = new Date(filters.customYear, filters.customMonth - 1, 1);
        const customEnd = new Date(filters.customYear, filters.customMonth, 0);
        params.append('start_date', formatDate(customStart));
        params.append('end_date', formatDateEnd(customEnd));
      } else {
        // Default fallback - last 30 days
        params.append('days', '30');
      }
      
      // Apply country filter
      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }
      
      // Apply currency filter
      if (filters.currency && filters.currency !== 'all') {
        params.append('currency', filters.currency);
      }
      
      // Apply merchant filter
      if (filters.merchant_id && filters.merchant_id !== 'all') {
        params.append('merchant_id', filters.merchant_id);
      }
      
      // Apply channel filter
      appendChannelParams(params, filters.channel);
      
      const response = await axios.get(`${API}/merchants/stats/top?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      setTopMerchants(response.data.top_merchants || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      console.error('Error fetching top merchants:', err);
      setTopMerchants([]);
    }
  };

  // Re-run the three main Overview fetches (KPIs + dashboard + top merchants).
  // Used by the "Data temporarily unavailable" retry when a server-side analytics
  // timeout (HTTP 503) is surfaced instead of a false $0.
  const reloadAll = () => {
    const controller = new AbortController();
    fetchKpis(controller.signal);
    fetchAnalyticsData(controller.signal);
    fetchTopMerchants(controller.signal);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      country: 'all',
      currency: 'all',
      merchant_id: 'all',
      channel: [],
      period: 'thismonth',
      customMonth: new Date().getMonth() + 1,
      customYear: new Date().getFullYear()
    });
  };

  if (!kpiMetrics && !analyticsData && (kpiLoading || loading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
        <div className="w-full mx-auto p-3 sm:p-4 lg:p-6 xl:p-8 space-y-4 sm:space-y-6 lg:space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                Overview
              </h1>
              <p className="text-muted-foreground mt-2">Loading...</p>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (analyticsUnavailable) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
        <div className="w-full mx-auto p-3 sm:p-4 lg:p-6 xl:p-8 space-y-4 sm:space-y-6 lg:space-y-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <BarChart3 className="w-8 h-8 text-blue-500" />
              Overview
            </h1>
          </div>
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="py-16">
              <DataUnavailable onRetry={reloadAll} testId="overview-unavailable" />
              <p className="text-center text-xs text-muted-foreground mt-2">
                The dashboard query took too long to respond. This is usually temporary — please retry.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
        <div className="w-full mx-auto p-3 sm:p-4 lg:p-6 xl:p-8 space-y-4 sm:space-y-6 lg:space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                Overview
              </h1>
              <p className="text-red-600 mt-2">{error}</p>
            </div>
            <button 
              onClick={fetchAnalyticsData}
              className="px-4 py-2 text-white rounded transition-colors"
              style={{ backgroundColor: theme.buttonColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = theme.buttonHoverColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonColor}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!kpiMetrics && !analyticsData) {
    return null;
  }

  // KPI cards bind to the fast kpiMetrics (paints first); charts/tables bind to the
  // slower full-dashboard payload, rendering empty until it arrives.
  const metrics = kpiMetrics || analyticsData?.metrics || {};
  const {
    monthly_revenue = [], daily_revenue = [], hourly_revenue = [], payment_methods = [],
    processors = [], countries = [], currencies = [], transaction_status = [],
    result_codes = [], recent_activity = [], transaction_types = [], channels = []
  } = analyticsData || {};

  const platformDisplayName = (!selectedPlatform || selectedPlatform === 'all')
    ? 'All Platforms'
    : (currentPlatform?.name || 'Platform');

  const exportOverviewPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress('Preparing report…');
    await new Promise(r => setTimeout(r, 250));
    try {
      const [{ jsPDF }, html2canvasMod] = await Promise.all([
        import('jspdf'),
        import('html2canvas-pro'),
      ]);
      const html2canvas = html2canvasMod.default;

      const el = pdfContentRef.current;
      if (!el) throw new Error('Nothing to export');

      // Let charts/layout settle before capture.
      setExportProgress('Rendering dashboard…');
      await new Promise(r => setTimeout(r, 700));

      const captureW = el.scrollWidth;
      const captureH = el.scrollHeight;
      const canvas = await html2canvas(el, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: captureW,
        height: captureH,
        windowWidth: captureW,
        windowHeight: captureH,
        scrollX: 0,
        scrollY: 0,
        imageTimeout: 8000,
        ignoreElements: (node) => {
          try {
            if (node.nodeType !== 1) return false;
            const tag = node.tagName;
            if (tag === 'CANVAS' && (node.width === 0 || node.height === 0)) return true;
            if ((tag === 'IMG' || tag === 'CANVAS' || tag === 'svg') &&
                node.offsetParent !== null &&
                node.offsetWidth === 0 && node.offsetHeight === 0) return true;
          } catch (e) { /* ignore */ }
          return false;
        },
      });

      setExportProgress('Building PDF…');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableW = pageW - margin * 2;

      // Report header on page 1.
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.setTextColor(30, 30, 30);
      pdf.text(platformDisplayName, margin, margin + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Overview Report · Generated ${new Date().toLocaleString()}`, margin, margin + 10);
      pdf.setDrawColor(220, 220, 220);
      pdf.line(margin, margin + 12.5, pageW - margin, margin + 12.5);
      const firstPageTop = margin + 15;

      const imgH = (canvas.height * usableW) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      // Paginate a tall capture across A4 pages (header offset on page 1).
      let pageTop = firstPageTop;
      let consumed = 0;
      while (consumed < imgH - 0.5) {
        const avail = pageH - pageTop - margin;
        pdf.addImage(imgData, 'JPEG', margin, pageTop - consumed, usableW, imgH);
        consumed += avail;
        if (consumed < imgH - 0.5) {
          pdf.addPage();
          pageTop = margin;
        }
      }

      setExportProgress('Saving PDF…');
      const pdfBlob = pdf.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      const namePart = platformDisplayName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      a.download = `overview_${namePart}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF export failed. Please try again.');
    } finally {
      setExportProgress('');
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="overview-export-loading-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Exporting to PDF</h3>
              <p className="text-sm text-gray-500 mt-1" data-testid="overview-export-progress-text">{exportProgress || 'Please wait while we prepare your file...'}</p>
            </div>
          </div>
        </div>
      )}
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
            <BarChart3 className="w-8 h-8 text-blue-500" />
            Overview
          </h1>
          <p className="text-muted-foreground mt-2">
            {selectedPlatform === 'all' ? (
              'Monitor all platforms payment gateway performance and key metrics'
            ) : currentPlatform ? (
              <>Monitor <span className="font-semibold text-blue-600">{currentPlatform.name}</span> payment gateway performance and key metrics</>
            ) : (
              'Monitor your payment gateway performance and key metrics'
            )}
          </p>
        </div>
        <div className="flex items-center space-x-6">
          {/* System Status Banner - Shows red warning if last transaction is older than 5 minutes */}
          {isTransactionStale(lastTransactionTime, currentPlatform?.timezone) ? (
            <div className="flex flex-col items-center px-4 py-2 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl border border-red-300" data-testid="system-status-warning">
              <div className="flex items-center">
                <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-red-700 font-medium text-sm">No Recent Transactions</span>
              </div>
              {lastTransactionTime && (
                <div className="text-xs text-red-600 mt-1">
                  Last transaction: {formatTimeDiff(lastTransactionTime, currentPlatform?.timezone)} ({formatInLocalTimezone(lastTransactionTime, currentPlatform?.timezone)})
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200" data-testid="system-status-operational">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse" />
                <span className="text-green-700 font-medium text-sm">All Systems Operational</span>
              </div>
              {lastTransactionTime && (
                <div className="text-xs text-gray-500 mt-1">
                  Last transaction: {formatInLocalTimezone(lastTransactionTime, currentPlatform?.timezone)}
                </div>
              )}
            </div>
          )}
          <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border" data-testid="yearly-distinct-counts">
            <span className="font-medium text-gray-700">{yearlyDistinctCounts.countries_count ?? '—'}</span> Countries •
            <span className="font-medium text-gray-700"> {yearlyDistinctCounts.currencies_count ?? '—'}</span> Currencies •
            <span className="font-medium text-gray-700"> {yearlyDistinctCounts.processors_count ?? '—'}</span> Processors
          </div>
          <Button
            onClick={exportOverviewPdf}
            disabled={isExporting}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            data-testid="export-overview-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            Export to PDF
          </Button>
        </div>
      </div>

        {/* PDF capture container: everything below the header */}
        <div ref={pdfContentRef} className="space-y-6">
        {/* Filters Section */}
        <Card className="border-0 shadow-md bg-white">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100 p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 flex-wrap">
              <CardTitle className="flex items-center gap-3 text-gray-800">
                <div className="p-2 bg-blue-500 rounded-lg text-white">
                  <Filter className="h-5 w-5" />
                </div>
                Filters
                {(kpiLoading || loading) && (kpiMetrics || analyticsData) && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600" data-testid="overview-filters-updating">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating…
                  </span>
                )}
              </CardTitle>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">Period:</label>
                <Select value={filters.period} onValueChange={(value) => handleFilterChange('period', value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7days">Last 7 days</SelectItem>
                    <SelectItem value="thismonth">This month</SelectItem>
                    <SelectItem value="thisyear">This year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Month/Year Selectors - Moved right after Period */}
              {filters.period === 'custom' && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium whitespace-nowrap">Month:</label>
                    <Select 
                      value={filters.customMonth.toString()} 
                      onValueChange={(value) => handleFilterChange('customMonth', parseInt(value))}
                    >
                      <SelectTrigger className="w-20 sm:w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium whitespace-nowrap">Year:</label>
                    <Select 
                      value={filters.customYear.toString()} 
                      onValueChange={(value) => handleFilterChange('customYear', parseInt(value))}
                    >
                      <SelectTrigger className="w-20 sm:w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 10 }, (_, i) => {
                          const year = new Date().getFullYear() - 5 + i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">Channel:</label>
                <MultiSelect
                  options={[
                    { code: 'All', name: 'All Channels' },
                    ...(reference.channels || [])
                  ]}
                  value={filters.channel}
                  onChange={(value) => {
                    if (value.includes('All')) {
                      handleFilterChange('channel', []);
                    } else {
                      handleFilterChange('channel', value);
                    }
                  }}
                  placeholder="All Channels"
                  className="w-40"
                  maxDisplayItems={1}
                  dataTestid="overview-channel-filter"
                  optionTestidPrefix="overview-channel"
                  renderOption={(ch) => (ch.code === 'All' ? 'All Channels' : (ch.name || ch.code))}
                  renderValue={(ch) => (ch.code === 'All' ? 'All Channels' : (ch.name || ch.code))}
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">Country:</label>
                <Select value={filters.country || "all"} onValueChange={(value) => handleFilterChange('country', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-40 sm:w-48">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {(availableFilters.countries || []).map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">Currency:</label>
                <Select value={filters.currency || "all"} onValueChange={(value) => handleFilterChange('currency', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-28 sm:w-32" data-testid="overview-currency-filter">
                    <SelectValue placeholder="All">
                      {filters.currency && filters.currency !== 'all' 
                        ? formatCurrencySelector(filters.currency)
                        : "All Currencies"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Currencies</SelectItem>
                    {(reference.currencies || []).map((currency) => (
                      <SelectItem key={currency.code} value={currency.code}>
                        {formatCurrencySelector(currency.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Only show merchant filter for specific platforms */}
              {selectedPlatform && selectedPlatform !== 'all' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium whitespace-nowrap">Merchant:</label>
                  <MerchantSearchSelect 
                    merchants={availableFilters.merchants}
                    value={filters.merchant_id || "all"}
                    onChange={(value) => handleFilterChange('merchant_id', value)}
                    placeholder="All Merchants"
                  />
                </div>
              )}

              {(filters.country && filters.country !== 'all' || filters.currency && filters.currency !== 'all' || filters.merchant_id && filters.merchant_id !== 'all' || (filters.channel && filters.channel.length > 0)) && (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

        {/* Key Metrics Cards */}
        <div className="relative">
          {(kpiLoading || loading) && (kpiMetrics || analyticsData) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl" data-testid="overview-cards-loading">
              <span className="inline-flex items-center gap-2 text-blue-600 font-semibold">
                <Loader2 className="h-5 w-5 animate-spin" />
                Recalculating…
              </span>
            </div>
          )}
          <div className={`grid gap-4 sm:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 transition-opacity duration-200 ${(kpiLoading || loading) && (kpiMetrics || analyticsData) ? 'opacity-40' : ''}`}>
          <MetricCard
            title="Total Revenue"
            value={formatCurrencyForOverview(metrics.total_revenue, filters.currency)}
            icon={DollarSign}
            testId="total-revenue-value"
            trend={(filters.period === 'thismonth' || filters.period === 'custom') && metrics.revenue_growth != null ? (metrics.revenue_growth > 0 ? "up" : metrics.revenue_growth < 0 ? "down" : "flat") : null}
            trendValue={(filters.period === 'thismonth' || filters.period === 'custom') ? metrics.revenue_growth : null}
            subtitle={
              <div className="text-sm font-medium text-gray-600">
                {filters.currency && filters.currency !== 'all' ? `${filters.currency.toUpperCase()} total` : 'Multi-currency total'}
              </div>
            }
          />
          <MetricCard
            title="Approved Transactions"
            value={formatNumberForOverview(metrics.total_transactions)}
            icon={CreditCard}
            testId="approved-transactions-value"
            trend={(filters.period === 'thismonth' || filters.period === 'custom') && metrics.transaction_growth != null ? (metrics.transaction_growth > 0 ? "up" : metrics.transaction_growth < 0 ? "down" : "flat") : null}
            trendValue={(filters.period === 'thismonth' || filters.period === 'custom') ? metrics.transaction_growth : null}
            subtitle={
              <div className="text-sm text-gray-600">
                <span className="font-medium">{formatNumberForOverview(metrics.all_transactions || 0)}</span>
                <span className="ml-1">total transactions</span>
              </div>
            }
          />
          <MetricCard
            title="Order Success Rate"
            value={`${metrics.order_success_rate.toFixed(1)}%`}
            icon={CheckCircle}
            testId="order-success-rate-value"
            subtitle={
              <div className="space-y-2">
                <div className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">{formatNumberForOverview(metrics.approved_orders)}</span>
                  <span className="text-xs text-green-600 ml-1">approved orders</span>
                </div>
                <div className="text-sm text-gray-600 border-t pt-2">
                  <div className="flex items-center justify-between">
                    <span>Transaction Success Rate:</span>
                    <span className="font-medium">{metrics.success_rate.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            }
          />
          <MetricCard
            title="Avg Transaction"
            value={formatCurrencyForOverview(metrics.avg_transaction_value, filters.currency)}
            icon={Users}
            testId="avg-transaction-value"
            trend={(filters.period === 'thismonth' || filters.period === 'custom') && metrics.avg_transaction_growth != null ? (metrics.avg_transaction_growth > 0 ? "up" : metrics.avg_transaction_growth < 0 ? "down" : "flat") : null}
            trendValue={(filters.period === 'thismonth' || filters.period === 'custom') ? metrics.avg_transaction_growth : null}
            subtitle={
              <div className="text-sm font-medium text-gray-600">
                {filters.currency && filters.currency !== 'all' ? `${filters.currency.toUpperCase()} equivalent` : 'USD equivalent'}
              </div>
            }
          />
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid gap-4 sm:gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-2">
          <Card className="border-0 shadow-lg bg-white">
            <RevenueByProcessorChart 
              selectedPlatform={selectedPlatform}
              currency={filters.currency}
              merchantId={filters.merchant_id}
              backendUrl={backendUrl}
              mainFilterPeriod={filters.period}
              mainFilterMonth={filters.customMonth}
              mainFilterYear={filters.customYear}
              channel={filters.channel}
            />
          </Card>
          <Card className="border-0 shadow-lg bg-white">
            <MemoizedHourlyRevenueChart 
              data={hourly_revenue || []} 
              token={token}
              selectedPlatform={selectedPlatform}
              customPeriod={filters.period === 'custom'}
              customMonth={filters.customMonth}
              customYear={filters.customYear}
              currency={filters.currency}
              merchantId={filters.merchant_id}
              mainFilterPeriod={filters.period}
              backendUrl={backendUrl}
              channel={filters.channel}
            />
          </Card>
        </div>

      {/* Monthly Revenue Section */}
      <div className="w-full">
        <RevenueChart 
          currency={filters.currency} 
          merchantId={filters.merchant_id}
          selectedPlatform={selectedPlatform}
          backendUrl={backendUrl}
          channel={filters.channel}
        />
      </div>

      <div className="w-full">
        <TopMerchants merchants={topMerchants} currency={filters.currency} />
      </div>

      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        <PaymentMethodsChart data={payment_methods} currency={filters.currency} paymentMethodLogos={paymentMethodLogos} />
        <ProcessorChart data={processors} currency={filters.currency} />
      </div>

      {/* Countries and Currency Breakdown - Top Row */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        <CountryChart data={countries} currency={filters.currency} />
        <CurrencyBreakdown currencies={currencies} currency={filters.currency} />
      </div>

      {/* Transaction Types and Channels */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        <TransactionTypeChart data={transaction_types} currency={filters.currency} />
        <ChannelChart data={channels} currency={filters.currency} />
      </div>

      {/* Transaction Status and Result Codes - Bottom Row */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
            <CardTitle className="flex items-center gap-3 text-gray-800">
              <div className="p-2 bg-blue-500 rounded-lg text-white">
                <Activity className="h-5 w-5" />
              </div>
              Transaction Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {transaction_status
                .filter(hasBreakdownValue)
                .sort((a, b) => b.percentage - a.percentage)
                .map((status, index) => {
                  // Map status to display name and color
                  const getStatusDisplay = (statusCode) => {
                    const statusMap = {
                      'completed': { name: 'Completed', color: 'bg-green-500' },
                      'pending': { name: 'Pending', color: 'bg-yellow-500' },
                      'processing': { name: 'Processing', color: 'bg-blue-400' },
                      'initiated': { name: 'Initiated', color: 'bg-gray-400' },
                      'failed': { name: 'Failed', color: 'bg-red-500' },
                      'failed_processor': { name: 'Failed Processor', color: 'bg-red-500' },
                      'failed_3d_secure': { name: 'Failed 3D Secure', color: 'bg-orange-500' },
                      'failed_risk_score': { name: 'Failed Risk Score', color: 'bg-rose-500' },
                      'refunded': { name: 'Refunded', color: 'bg-purple-500' },
                      'cancelled': { name: 'Cancelled', color: 'bg-gray-500' }
                    };
                    return statusMap[statusCode] || { name: statusCode?.replace(/_/g, ' '), color: 'bg-gray-500' };
                  };
                  
                  const statusInfo = getStatusDisplay(status.status);
                  
                  return (
                    <div key={status.status} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/30 transition-all duration-200">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${statusInfo.color}`} />
                        <span className="font-medium text-gray-900 capitalize">{statusInfo.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">{status.count.toLocaleString()}</div>
                        <div className="text-xs font-medium text-gray-600">{status.percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
            <CardTitle className="flex items-center gap-3 text-gray-800">
              <div className="p-2 bg-blue-500 rounded-lg text-white">
                <BarChart3 className="h-5 w-5" />
              </div>
              Result Codes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {result_codes.filter(hasBreakdownValue).map((code, index) => (
                <div key={code.result_code} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/30 transition-all duration-200">
                  <div className="flex items-center space-x-3">
                    <div className={`px-2 py-1 rounded-lg text-xs font-bold text-white ${
                      code.is_approved ? 'bg-green-500' : 'bg-red-500'
                    }`}>
                      {code.result_code}
                    </div>
                    <div>
                      {code.description && (
                        <div className="text-sm font-medium text-gray-800">{code.description}</div>
                      )}
                      <div className="text-sm text-gray-600">
                        ${code.total_amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{code.count.toLocaleString()}</div>
                    <div className="text-xs font-medium text-gray-600">{code.percentage}%</div>
                  </div>
                </div>
              ))}
              {result_codes.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                    <BarChart3 className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">No result code data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
};

export default Overview;